import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Typography
} from "@material-tailwind/react";
import Cookies from 'js-cookie';
import axios from 'axios'; // Import axios
import { LanguageProvider } from './contexts/LanguageContext';
import NavigationMenu from './components/NavigationMenu';
import Home from './components/Home';
import SearchPage from './components/SearchPage';
import ChatScreen from './components/ChatScreen';
import ShoppingList from './components/ShoppingList';
import Profile from './components/Profile';
import Login from './components/Login';
import { brandLogo as logo, brandName, brandify, isMaisterchefGermanTheme } from './utils/branding';
import Recipes from './components/Recipes';
import BarcodeScanPage from './components/BarcodeScanPage'
import NutritionGoalsPage from './components/NutritionGoalsPage'
import { useLanguage } from './contexts/LanguageContext';
import { languages } from './languages'; // Import languages for t
import ReactCountryFlag from "react-country-flag";
import Signup from './components/Signup';
import CancelSubscription from './components/CancelSubscription'; // Import the new component
import LanguageFooter from './components/LanguageFooter'; // Import the LanguageFooter component
import FeatureWelcomeDialog from './components/FeatureWelcomeDialog';

const ProtectedRoute = ({ isLoggedIn, redirectPath = '/login' }) => {
  if (!isLoggedIn) {
    return <Navigate to={redirectPath} replace />;
  }
  return <Outlet />;
};

// Component to check session ID from URL and redirect to signup if needed
const SessionCheck = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [hasChecked, setHasChecked] = useState(false);

  useEffect(() => {
    // Only check if user is not logged in and we haven't checked yet
    const isLoggedIn = !!Cookies.get('isLoggedIn');
    if (isLoggedIn || hasChecked) {
      return;
    }

    const params = new URLSearchParams(location.search);
    const sessionId = params.get('s');

    // Only check if we're not already on signup or login page
    if (sessionId && !location.pathname.includes('/signup') && !location.pathname.includes('/login')) {
      const checkSession = async () => {
        try {
          const response = await fetch(`/api/login/check-session?sessionId=${sessionId}`);
          const data = await response.json();

          if (response.ok && data.redirectToSignup) {
            // Session is valid but user doesn't exist, redirect to signup
            console.log('Session ID valid but user not found, redirecting to signup');
            navigate(`/signup?s=${sessionId}`);
          }
        } catch (error) {
          console.error('Error checking session:', error);
        } finally {
          setHasChecked(true);
        }
      };

      checkSession();
    } else {
      setHasChecked(true);
    }
  }, [location, navigate, hasChecked]);

  return children;
};

const App = () => {
  // Check for s= or m= parameters SYNCHRONOUSLY before initializing state
  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get('s');
  const msisdn = params.get('m');
  
  // Check if we're on the signup page
  const isOnSignupPage = window.location.pathname.includes('/signup');
  
  // Only clear cookies/localStorage if:
  // 1. There's a session/msisdn parameter AND
  // 2. We're on the signup page (fresh signup attempt) AND
  // 3. User is NOT already logged in (don't clear if they completed signup and are navigating)
  const existingLoginCookie = document.cookie.includes('isLoggedIn=true');
  
  if ((sessionId || msisdn) && isOnSignupPage && !existingLoginCookie) {
    console.log('Fresh signup attempt detected on signup page, clearing all cookies and localStorage');
    
    // Clear all cookies
    const allCookies = document.cookie.split(';');
    allCookies.forEach(cookie => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      if (name) {
        Cookies.remove(name);
        Cookies.remove(name, { path: '/' });
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
      }
    });
    
    // Clear all localStorage
    localStorage.clear();
    
    console.log('All cookies and localStorage cleared in App.js');
  } else if ((sessionId || msisdn) && !isOnSignupPage) {
    // If s= or m= is in URL but we're NOT on signup page, 
    // the user likely completed signup and clicked a link - don't clear anything
    console.log('Session/MSISDN parameter detected but not on signup page - keeping login state');
  }

  // Initialize login state
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    // Check cookie normally - we only clear on fresh signup attempts now
    return !!Cookies.get('isLoggedIn');
  });
  const [userCountry, setUserCountry] = useState(null);
  const [selectedContinent, setSelectedContinent] = useState('europe'); // Default to Europe
  const [showContinentSelector, setShowContinentSelector] = useState(false);

  // Set document title and meta description based on branding
  useEffect(() => {
    document.title = brandName;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', brandName);
  }, []);

  // Detect user's country
  useEffect(() => {
    const detectCountry = async () => {
      try {
        // Try using IP geolocation API
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        if (data.country_code) {
          setUserCountry(data.country_code.toUpperCase());
          return;
        }
      } catch (error) {
        console.log('Could not detect country via IP geolocation, trying timezone detection');
      }

      // Fallback: Try to detect from timezone
      try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        // Map timezones to countries for Arabic countries
        const timezoneToCountry = {
          'Asia/Bahrain': 'BH',
          'Asia/Riyadh': 'SA',
          'Asia/Baghdad': 'IQ',
          'Asia/Kuwait': 'KW',
          'Asia/Dubai': 'AE',
          'Asia/Muscat': 'OM',
          'Asia/Qatar': 'QA',
          'Asia/Aden': 'YE',
          'Asia/Amman': 'JO',
          'Asia/Beirut': 'LB',
          'Asia/Damascus': 'SY',
          'Asia/Gaza': 'PS',
          'Asia/Hebron': 'PS',
          'Africa/Cairo': 'EG'
        };
        
        if (timezoneToCountry[timezone]) {
          setUserCountry(timezoneToCountry[timezone]);
          return;
        }
      } catch (error) {
        console.log('Could not detect country from timezone');
      }
    };

    detectCountry();
  }, []);

  const handleLogin = () => {
    setIsLoggedIn(true);
  };

  const handleLogout = () => {
    Cookies.remove('isLoggedIn');
    setIsLoggedIn(false);
  };

  const { language, setLanguage } = useLanguage();

  // Set document direction based on language
  const arabicLanguages = ['arabic', 'bahrain-arabic', 'uae-arabic', 'sa-arabic', 'kw-arabic', 'qa-arabic', 'om-arabic', 'iq-arabic', 'jo-arabic', 'lb-arabic', 'sy-arabic', 'ps-arabic', 'ye-arabic', 'eg-arabic'];
  const isRTL = arabicLanguages.includes(language);
  
  const mcDeTheme = isMaisterchefGermanTheme(language);

  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = mcDeTheme ? 'de' : (isRTL ? 'ar' : 'en');
    document.documentElement.classList.toggle('theme-mc-de', mcDeTheme);
    document.body.classList.toggle('theme-mc-de', mcDeTheme);
    return () => {
      document.documentElement.classList.remove('theme-mc-de');
      document.body.classList.remove('theme-mc-de');
    };
  }, [language, isRTL, mcDeTheme]);

  // Map Belgian language codes to french and nederlands for translations
  let translationLanguage = language;
  if (language === 'befr' || language === 'be-fr') translationLanguage = 'french';
  if (language === 'benl' || language === 'be-nl') translationLanguage = 'nederlands';
  if (language === 'bh-ar' || language === 'bahrain-arabic') translationLanguage = 'arabic';
  if (language === 'bh-en' || language === 'bahrain-english') translationLanguage = 'english';
  if (language === 'uae-ar' || language === 'uae-arabic') translationLanguage = 'arabic';
  if (language === 'uae-en' || language === 'uae-english') translationLanguage = 'english';
  // Map all other Arabic country variants to arabic/english
  if (language === 'sa-ar' || language === 'sa-arabic') translationLanguage = 'arabic';
  if (language === 'sa-en' || language === 'sa-english') translationLanguage = 'english';
  if (language === 'kw-ar' || language === 'kw-arabic') translationLanguage = 'arabic';
  if (language === 'kw-en' || language === 'kw-english') translationLanguage = 'english';
  if (language === 'qa-ar' || language === 'qa-arabic') translationLanguage = 'arabic';
  if (language === 'qa-en' || language === 'qa-english') translationLanguage = 'english';
  if (language === 'om-ar' || language === 'om-arabic') translationLanguage = 'arabic';
  if (language === 'om-en' || language === 'om-english') translationLanguage = 'english';
  if (language === 'iq-ar' || language === 'iq-arabic') translationLanguage = 'arabic';
  if (language === 'iq-en' || language === 'iq-english') translationLanguage = 'english';
  if (language === 'jo-ar' || language === 'jo-arabic') translationLanguage = 'arabic';
  if (language === 'jo-en' || language === 'jo-english') translationLanguage = 'english';
  if (language === 'lb-ar' || language === 'lb-arabic') translationLanguage = 'arabic';
  if (language === 'lb-en' || language === 'lb-english') translationLanguage = 'english';
  if (language === 'sy-ar' || language === 'sy-arabic') translationLanguage = 'arabic';
  if (language === 'sy-en' || language === 'sy-english') translationLanguage = 'english';
  if (language === 'ps-ar' || language === 'ps-arabic') translationLanguage = 'arabic';
  if (language === 'ps-en' || language === 'ps-english') translationLanguage = 'english';
  if (language === 'ye-ar' || language === 'ye-arabic') translationLanguage = 'arabic';
  if (language === 'ye-en' || language === 'ye-english') translationLanguage = 'english';
  if (language === 'eg-ar' || language === 'eg-arabic') translationLanguage = 'arabic';
  if (language === 'eg-en' || language === 'eg-english') translationLanguage = 'english';
  if (language === 'pl-pl') translationLanguage = 'polish';
  if (language === 'arabic') translationLanguage = 'arabic';
  if (language === 'cyprus' || language === 'cy' || language === 'el-cy') translationLanguage = 'greek';
  const t = languages[translationLanguage] || languages['english']; // Get translations with fallback to English
  const [isOpen, setIsOpen] = useState(false);
  const [openUnsubscribeDialog, setOpenUnsubscribeDialog] = useState(false); // State for dialog

  const toggleDropdown = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    
    if (newIsOpen) {
      // For non-Arabic countries, show continent selector first
      if (!userCountry || !arabicCountries.includes(userCountry)) {
        setShowContinentSelector(true);
      }
    } else {
      // Reset continent selector when closing
      setShowContinentSelector(false);
    }
  };

  const changeLanguage = (newLanguage) => {
    setLanguage(newLanguage);
    setIsOpen(false);
    setShowContinentSelector(false);
  };

  const selectContinent = (continentCode) => {
    setSelectedContinent(continentCode);
    setShowContinentSelector(false);
  };

  const goBackToContinents = () => {
    setShowContinentSelector(true);
  };

  // Arabic-speaking Middle East countries where only Arabic and English should be shown
  const arabicCountries = [
    'BH', // Bahrain
    'SA', // Saudi Arabia
    'IQ', // Iraq
    'AE', // United Arab Emirates
    'KW', // Kuwait
    'QA', // Qatar
    'OM', // Oman
    'YE', // Yemen
    'JO', // Jordan
    'LB', // Lebanon
    'SY', // Syria
    'PS', // Palestine
    'EG'  // Egypt
  ];

  // Continent mapping: country codes to continents
  const countryToContinent = {
    // Europe
    'GB': 'europe', 'NL': 'europe', 'BE': 'europe', 'DE': 'europe', 'FR': 'europe',
    'ES': 'europe', 'PT': 'europe', 'IT': 'europe', 'PL': 'europe', 'RO': 'europe',
    'RS': 'europe', 'NO': 'europe', 'SE': 'europe', 'DK': 'europe', 'FI': 'europe',
    'GR': 'europe', 'AT': 'europe', 'CH': 'europe', 'IE': 'europe', 'CZ': 'europe',
    'HU': 'europe', 'BG': 'europe', 'HR': 'europe', 'SI': 'europe', 'SK': 'europe',
    'CY': 'europe',
    // Asia (excluding Arabic countries)
    'CN': 'asia', 'JP': 'asia', 'KR': 'asia', 'IN': 'asia', 'TH': 'asia',
    'VN': 'asia', 'ID': 'asia', 'PH': 'asia', 'MY': 'asia', 'SG': 'asia',
    // Africa (excluding Arabic countries)
    'ZA': 'africa', 'NG': 'africa', 'KE': 'africa', 'GH': 'africa', 'ET': 'africa',
    // North America
    'US': 'north-america', 'CA': 'north-america', 'MX': 'north-america',
    // South America
    'BR': 'south-america', 'AR': 'south-america', 'CL': 'south-america', 'CO': 'south-america',
    'PE': 'south-america', 'VE': 'south-america',
    // Oceania
    'AU': 'oceania', 'NZ': 'oceania', 'FJ': 'oceania'
  };

  // Languages grouped by continent
  const languagesByContinent = {
    europe: [
      { code: 'english', label: 'EN', flagCode: 'english' },
      { code: 'nederlands', label: 'NL', flagCode: 'nederlands' },
      { code: 'deutsch', label: 'DE', flagCode: 'deutsch' },
      { code: 'spanish', label: 'ES', flagCode: 'spanish' },
      { code: 'french', label: 'FR', flagCode: 'french' },
      { code: 'portuguese', label: 'PT', flagCode: 'portuguese' },
      { code: 'polish', label: 'PL', flagCode: 'polish' },
      { code: 'romanian', label: 'RO', flagCode: 'romanian' },
      { code: 'serbian', label: 'SR', flagCode: 'serbian' },
      { code: 'norwegian', label: 'NO', flagCode: 'norwegian' },
      { code: 'danish', label: 'DA', flagCode: 'danish' },
      { code: 'swedish', label: 'SV', flagCode: 'swedish' },
      { code: 'slovenian', label: 'SL', flagCode: 'slovenian' },
      { code: 'greek', label: 'EL', flagCode: 'greek' },
      { code: 'cyprus', label: 'CY', flagCode: 'cyprus' },
      { code: 'be-fr', label: 'BE-FR', flagCode: 'be-fr' },
      { code: 'be-nl', label: 'BE-NL', flagCode: 'be-nl' }
    ],
    'middle-east': [
      { code: 'bahrain-arabic', label: 'العربية', flagCode: 'bahrain-arabic' },
      { code: 'bahrain-english', label: 'BH-EN', flagCode: 'bahrain-english' },
      { code: 'uae-arabic', label: 'العربية', flagCode: 'uae-arabic' },
      { code: 'uae-english', label: 'UAE-EN', flagCode: 'uae-english' },
      { code: 'sa-arabic', label: 'العربية', flagCode: 'sa-arabic' },
      { code: 'sa-english', label: 'SA-EN', flagCode: 'sa-english' },
      { code: 'kw-arabic', label: 'العربية', flagCode: 'kw-arabic' },
      { code: 'kw-english', label: 'KW-EN', flagCode: 'kw-english' },
      { code: 'qa-arabic', label: 'العربية', flagCode: 'qa-arabic' },
      { code: 'qa-english', label: 'QA-EN', flagCode: 'qa-english' },
      { code: 'om-arabic', label: 'العربية', flagCode: 'om-arabic' },
      { code: 'om-english', label: 'OM-EN', flagCode: 'om-english' },
      { code: 'iq-arabic', label: 'العربية', flagCode: 'iq-arabic' },
      { code: 'iq-english', label: 'IQ-EN', flagCode: 'iq-english' },
      { code: 'jo-arabic', label: 'العربية', flagCode: 'jo-arabic' },
      { code: 'jo-english', label: 'JO-EN', flagCode: 'jo-english' },
      { code: 'lb-arabic', label: 'العربية', flagCode: 'lb-arabic' },
      { code: 'lb-english', label: 'LB-EN', flagCode: 'lb-english' },
      { code: 'sy-arabic', label: 'العربية', flagCode: 'sy-arabic' },
      { code: 'sy-english', label: 'SY-EN', flagCode: 'sy-english' },
      { code: 'ps-arabic', label: 'العربية', flagCode: 'ps-arabic' },
      { code: 'ps-english', label: 'PS-EN', flagCode: 'ps-english' },
      { code: 'ye-arabic', label: 'العربية', flagCode: 'ye-arabic' },
      { code: 'ye-english', label: 'YE-EN', flagCode: 'ye-english' },
      { code: 'eg-arabic', label: 'العربية', flagCode: 'eg-arabic' },
      { code: 'eg-english', label: 'EG-EN', flagCode: 'eg-english' }
    ],
    asia: [
      { code: 'english', label: 'EN', flagCode: 'english' },
      { code: 'arabic', label: 'العربية', flagCode: 'arabic' }
    ],
    africa: [
      { code: 'english', label: 'EN', flagCode: 'english' },
      { code: 'french', label: 'FR', flagCode: 'french' }
    ],
    'north-america': [
      { code: 'english', label: 'EN', flagCode: 'english' },
      { code: 'mx-es', label: 'MX - Español', flagCode: 'mx-es' },
      { code: 'spanish', label: 'ES', flagCode: 'spanish' },
      { code: 'french', label: 'FR', flagCode: 'french' }
    ],
    'south-america': [
      { code: 'spanish', label: 'ES', flagCode: 'spanish' },
      { code: 'portuguese', label: 'PT', flagCode: 'portuguese' },
      { code: 'english', label: 'EN', flagCode: 'english' }
    ],
    oceania: [
      { code: 'english', label: 'EN', flagCode: 'english' }
    ]
  };

  // Get available languages based on country
  const getAvailableLanguages = () => {
    if (userCountry && arabicCountries.includes(userCountry)) {
      // Country-specific language mappings for Arabic countries
      const countryLanguageMap = {
        'BH': [
          { code: 'bahrain-english', label: 'BH-EN', flagCode: 'bahrain-english' },
          { code: 'bahrain-arabic', label: 'العربية', flagCode: 'bahrain-arabic' }
        ],
        'SA': [
          { code: 'sa-english', label: 'SA-EN', flagCode: 'sa-english' },
          { code: 'sa-arabic', label: 'العربية', flagCode: 'sa-arabic' }
        ],
        'AE': [
          { code: 'uae-english', label: 'UAE-EN', flagCode: 'uae-english' },
          { code: 'uae-arabic', label: 'العربية', flagCode: 'uae-arabic' }
        ],
        'KW': [
          { code: 'kw-english', label: 'KW-EN', flagCode: 'kw-english' },
          { code: 'kw-arabic', label: 'العربية', flagCode: 'kw-arabic' }
        ],
        'QA': [
          { code: 'qa-english', label: 'QA-EN', flagCode: 'qa-english' },
          { code: 'qa-arabic', label: 'العربية', flagCode: 'qa-arabic' }
        ],
        'OM': [
          { code: 'om-english', label: 'OM-EN', flagCode: 'om-english' },
          { code: 'om-arabic', label: 'العربية', flagCode: 'om-arabic' }
        ],
        'IQ': [
          { code: 'iq-english', label: 'IQ-EN', flagCode: 'iq-english' },
          { code: 'iq-arabic', label: 'العربية', flagCode: 'iq-arabic' }
        ],
        'JO': [
          { code: 'jo-english', label: 'JO-EN', flagCode: 'jo-english' },
          { code: 'jo-arabic', label: 'العربية', flagCode: 'jo-arabic' }
        ],
        'LB': [
          { code: 'lb-english', label: 'LB-EN', flagCode: 'lb-english' },
          { code: 'lb-arabic', label: 'العربية', flagCode: 'lb-arabic' }
        ],
        'SY': [
          { code: 'sy-english', label: 'SY-EN', flagCode: 'sy-english' },
          { code: 'sy-arabic', label: 'العربية', flagCode: 'sy-arabic' }
        ],
        'PS': [
          { code: 'ps-english', label: 'PS-EN', flagCode: 'ps-english' },
          { code: 'ps-arabic', label: 'العربية', flagCode: 'ps-arabic' }
        ],
        'YE': [
          { code: 'ye-english', label: 'YE-EN', flagCode: 'ye-english' },
          { code: 'ye-arabic', label: 'العربية', flagCode: 'ye-arabic' }
        ],
        'EG': [
          { code: 'eg-english', label: 'EG-EN', flagCode: 'eg-english' },
          { code: 'eg-arabic', label: 'العربية', flagCode: 'eg-arabic' }
        ]
      };

      // Return country-specific languages if available
      if (countryLanguageMap[userCountry]) {
        return countryLanguageMap[userCountry];
      }

      // Fallback to standard Arabic and English
      return [
        { code: 'english', label: 'EN', flagCode: 'english' },
        { code: 'arabic', label: 'العربية', flagCode: 'arabic' }
      ];
    }
    
    // For non-Arabic countries, return languages for selected continent (default: europe)
    return languagesByContinent[selectedContinent] || languagesByContinent['europe'];
  };

  // Get continents list
  const getContinents = () => {
    if (mcDeTheme) {
      return [
        { code: 'europe', label: t.continentEurope || 'Europa' },
        { code: 'middle-east', label: t.continentMiddleEast || 'Naher Osten' },
        { code: 'asia', label: t.continentAsia || 'Asien' },
        { code: 'africa', label: t.continentAfrica || 'Afrika' },
        { code: 'north-america', label: t.continentNorthAmerica || 'Nordamerika' },
        { code: 'south-america', label: t.continentSouthAmerica || 'Südamerika' },
        { code: 'oceania', label: t.continentOceania || 'Ozeanien' }
      ];
    }
    return [
      { code: 'europe', label: `🌍 ${t.continentEurope || 'Europe'}` },
      { code: 'middle-east', label: `🌍 ${t.continentMiddleEast || 'Middle East'}` },
      { code: 'asia', label: `🌏 ${t.continentAsia || 'Asia'}` },
      { code: 'africa', label: `🌍 ${t.continentAfrica || 'Africa'}` },
      { code: 'north-america', label: `🌎 ${t.continentNorthAmerica || 'North America'}` },
      { code: 'south-america', label: `🌎 ${t.continentSouthAmerica || 'South America'}` },
      { code: 'oceania', label: `🌏 ${t.continentOceania || 'Oceania'}` }
    ];
  };

  // Detect continent from country and set default
  useEffect(() => {
    if (userCountry) {
      if (!arabicCountries.includes(userCountry)) {
        const detectedContinent = countryToContinent[userCountry] || 'europe';
        setSelectedContinent(detectedContinent);
      }
    } else {
      // Default to europe if country not detected
      setSelectedContinent('europe');
    }
  }, [userCountry]);

  const LanguageFlag = ({ code }) => {
    const countryCodeMap = {
      english: 'GB',
      nederlands: 'NL',
      deutsch: 'DE',
      spanish: 'ES',
      french: 'FR',
      arabic: 'SA',
      serbian: 'RS',
      romanian: 'RO',
      portuguese: 'PT',
      polish: 'PL',
      norwegian: 'NO',
      danish: 'DK',
      swedish: 'SE',
      slovenian: 'SI',
      greek: 'GR',
      cyprus: 'CY',
      befr: 'BE',
      benl: 'BE',
      'be-fr': 'BE',
      'be-nl': 'BE',
      'bahrain-arabic': 'BH',
      'bahrain-english': 'BH',
      'uae-arabic': 'AE',
      'uae-english': 'AE',
      'sa-arabic': 'SA',
      'sa-english': 'SA',
      'kw-arabic': 'KW',
      'kw-english': 'KW',
      'qa-arabic': 'QA',
      'qa-english': 'QA',
      'om-arabic': 'OM',
      'om-english': 'OM',
      'iq-arabic': 'IQ',
      'iq-english': 'IQ',
      'jo-arabic': 'JO',
      'jo-english': 'JO',
      'lb-arabic': 'LB',
      'lb-english': 'LB',
      'sy-arabic': 'SY',
      'sy-english': 'SY',
      'ps-arabic': 'PS',
      'ps-english': 'PS',
      'ye-arabic': 'YE',
      'ye-english': 'YE',
      'eg-arabic': 'EG',
      'eg-english': 'EG',
      'mx-es': 'MX'
    };

    const countryCode = countryCodeMap[code] || 'GB';
    return (
      <ReactCountryFlag
        countryCode={countryCode}
        svg
        style={{
          fontSize: '2em',
          lineHeight: '2em',
        }}
        aria-label={countryCode}
      />
    );
  };

  // Unsubscribe handler adapted from Profile.js (simplified without alerts)
  const handleConfirmUnsubscribe = async () => {
    try {
      const accountSessionId = localStorage.getItem('accountSessionId');
      if (!accountSessionId) {
        console.error('Account Session ID not found in localStorage.');
        // Optionally inform the user
        setOpenUnsubscribeDialog(false);
        return;
      }
      const url = `https://signup.souschaif.com/msp/subscription/terminate?sessionId=${accountSessionId}&apiCode=souschaif`;
      console.log('Calling unsubscribe URL:', url);
      await axios.get(url);
      console.log(t.unsubscribeSuccess || "Unsubscription successful.");
      // Optionally: Trigger a state update or redirect if needed after unsubscribing
    } catch (error) {
      console.error('Error during unsubscription:', error);
      console.error(t.unsubscribeError || "Error during unsubscription.");
    } finally {
      setOpenUnsubscribeDialog(false);
    }
  };


  // Norwegian disclaimer banner
  const isNorwegian = language === 'norwegian' || language === 'no' || language === 'no-no';

  return (
    <Router>
      <div className={`flex flex-col min-h-screen ${mcDeTheme ? 'mc-de-shell' : 'bg-gray-50'}`}>

        {/* Norwegian subscription disclaimer */}
        {isNorwegian && (
          <div className="bg-black text-white text-xs py-2 px-4 text-center">
            {brandName}, abonnement 199kr/måned, for å stoppe send "stopp" til 2210. Kundservice: 22120403 help@amintabv.com Kun 18+.
          </div>
        )}

        <header className={mcDeTheme
          ? 'mc-de-header'
          : 'top-0 left-0 right-0 z-10 h-16 flex justify-between items-center px-4'
        }>
          {mcDeTheme ? (
            <div className="mc-de-brand">
              <img src={logo} alt={brandName} />
              <div>
                <span className="mc-de-wordmark">{brandName}</span>
                <span className="mc-de-tagline">Rezepte und Inspiration</span>
              </div>
            </div>
          ) : (
            <img src={logo} alt={brandName} className="h-12" />
          )}
          <div className='flex gap-2 items-center'>
            <div className="relative">
              <button onClick={toggleDropdown} className={mcDeTheme ? 'mc-de-lang-btn focus:outline-none' : 'focus:outline-none flex items-center'}>
                <div className="w-8 h-8 overflow-hidden">
                  <LanguageFlag code={language} />
                </div>
                <span className="ltr:ml-2 rtl:mr-2 text-sm font-medium">
                  {(() => {
                    const languageCodeMap = {
                      'english': 'EN',
                      'nederlands': 'NL',
                      'deutsch': 'DE',
                      'spanish': 'ES',
                      'french': 'FR',
                      'arabic': 'العربية',
                      'serbian': 'SR',
                      'romanian': 'RO',
                      'portuguese': 'PT',
                      'polish': 'PL',
                      'norwegian': 'NO',
                      'danish': 'DA',
                      'swedish': 'SV',
                      'slovenian': 'SL',
                      'greek': 'EL',
                      'cyprus': 'CY',
                      'be-fr': 'BE-FR',
                      'be-nl': 'BE-NL',
                      'bahrain-arabic': 'العربية',
                      'bahrain-english': 'BH-EN',
                      'uae-arabic': 'العربية',
                      'uae-english': 'UAE-EN',
                      'sa-arabic': 'العربية',
                      'sa-english': 'SA-EN',
                      'kw-arabic': 'العربية',
                      'kw-english': 'KW-EN',
                      'qa-arabic': 'العربية',
                      'qa-english': 'QA-EN',
                      'om-arabic': 'العربية',
                      'om-english': 'OM-EN',
                      'iq-arabic': 'العربية',
                      'iq-english': 'IQ-EN',
                      'jo-arabic': 'العربية',
                      'jo-english': 'JO-EN',
                      'lb-arabic': 'العربية',
                      'lb-english': 'LB-EN',
                      'sy-arabic': 'العربية',
                      'sy-english': 'SY-EN',
                      'ps-arabic': 'العربية',
                      'ps-english': 'PS-EN',
                      'ye-arabic': 'العربية',
                      'ye-english': 'YE-EN',
                      'eg-arabic': 'العربية',
                      'eg-english': 'EG-EN',
                      'mx-es': 'MX'
                    };
                    return languageCodeMap[language] || 'EN';
                  })()}
                </span>
              </button>
              {isOpen && (
                <>
                  {/* Mobile overlay backdrop */}
                  <div className="fixed inset-0 bg-black bg-opacity-50 z-40 sm:hidden" onClick={toggleDropdown}></div>
                  {/* Dropdown - centered on mobile, positioned on desktop with viewport awareness */}
                  <div className={`language-dropdown fixed sm:absolute top-1/2 sm:top-full left-1/2 -translate-x-1/2 sm:translate-x-0 sm:translate-y-0 -translate-y-1/2 sm:translate-y-0 mt-0 sm:mt-2 bg-white rounded-md shadow-lg w-[85vw] sm:w-[400px] sm:max-w-[min(400px,calc(100vw-2rem))] max-h-[80vh] sm:max-h-[500px] overflow-y-auto z-50 ${isRTL ? 'sm:left-0 sm:right-auto' : 'sm:right-0 sm:left-auto'}`}>
                  {userCountry && arabicCountries.includes(userCountry) ? (
                    // For Arabic countries: show languages directly in 2 columns
                    <div className="grid grid-cols-2 gap-0 p-2 sm:p-2">
                      {getAvailableLanguages().map((lang) => (
                        <button 
                          key={lang.code} 
                          onClick={() => changeLanguage(lang.code)} 
                          className="flex items-center w-full px-2 sm:px-3 py-2.5 sm:py-2 ltr:text-left rtl:text-right hover:bg-gray-100 rounded touch-manipulation min-w-0"
                        >
                          <div className="w-6 h-6 sm:w-8 sm:h-8 overflow-hidden flex-shrink-0">
                            {lang.code === 'be-fr' || lang.code === 'be-nl' ? (
                              <ReactCountryFlag countryCode="BE" svg style={{ fontSize: '1.5em', lineHeight: '1.5em' }} />
                            ) : (
                              <LanguageFlag code={lang.flagCode} />
                            )}
                    </div>
                          <span className="ltr:ml-1.5 sm:ltr:ml-2 rtl:mr-1.5 sm:rtl:mr-2 text-xs sm:text-sm truncate">{lang.label}</span>
                  </button>
                      ))}
                    </div>
                  ) : showContinentSelector ? (
                    // For non-Arabic countries: show continent selector first
                    <div className="p-2 sm:p-2">
                      {getContinents().map((continent) => (
                        <button
                          key={continent.code}
                          onClick={() => selectContinent(continent.code)}
                          className="flex items-center w-full px-3 sm:px-4 py-2.5 sm:py-2 ltr:text-left rtl:text-right hover:bg-gray-100 rounded touch-manipulation"
                        >
                          {mcDeTheme ? (
                            <span className="text-sm sm:text-base">{continent.label}</span>
                          ) : (
                            <>
                              <span className="text-base sm:text-lg mr-2">{continent.label.split(' ')[0]}</span>
                              <span className="text-sm sm:text-base">{continent.label.substring(continent.label.indexOf(' ') + 1)}</span>
                            </>
                          )}
                  </button>
                      ))}
                    </div>
                  ) : (
                    // Show languages for selected continent with back button in 2 columns
                    <div className="p-2 sm:p-2">
                      <button
                        onClick={goBackToContinents}
                        className="flex items-center w-full px-3 sm:px-4 py-2.5 sm:py-2 ltr:text-left rtl:text-right hover:bg-gray-100 border-b border-gray-200 mb-2 rounded touch-manipulation"
                      >
                        <span className="mr-2">←</span>
                        <span className="text-sm sm:text-base">{t.backToContinents || 'Back to Continents'}</span>
                  </button>
                      <div className="grid grid-cols-2 gap-0">
                        {getAvailableLanguages().map((lang) => (
                          <button 
                            key={lang.code} 
                            onClick={() => changeLanguage(lang.code)} 
                            className="flex items-center w-full px-2 sm:px-3 py-2.5 sm:py-2 ltr:text-left rtl:text-right hover:bg-gray-100 rounded touch-manipulation min-w-0"
                          >
                            <div className="w-6 h-6 sm:w-8 sm:h-8 overflow-hidden flex-shrink-0">
                              {lang.code === 'be-fr' || lang.code === 'be-nl' ? (
                      <ReactCountryFlag countryCode="BE" svg style={{ fontSize: '1.5em', lineHeight: '1.5em' }} />
                              ) : (
                                <LanguageFlag code={lang.flagCode} />
                              )}
                    </div>
                            <span className="ltr:ml-1.5 sm:ltr:ml-2 rtl:mr-1.5 sm:rtl:mr-2 text-xs sm:text-sm truncate">{lang.label}</span>
                  </button>
                        ))}
                    </div>
                    </div>
                  )}
                </div>
                </>
              )}
            </div>
            {isLoggedIn && (
              <Button onClick={handleLogout} size="sm" variant='text' className={`flex justify-center items-center text-xs ${mcDeTheme ? 'mc-de-logout' : ''}`}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 9V5.25A2.25 2.25 0 0 1 10.5 3h6a2.25 2.25 0 0 1 2.25 2.25v13.5A2.25 2.25 0 0 1 16.5 21h-6a2.25 2.25 0 0 1-2.25-2.25V15M12 9l3 3m0 0-3 3m3-3H2.25" />
                </svg>
              </Button>
            )}
          </div>
        </header>


        <main className={`flex-grow mb-8 ${mcDeTheme ? 'mc-de-main' : ''}`}>
          <div className="max-w-7xl mx-auto px-4">
            <SessionCheck>
              <Routes>
                <Route
                  path="/login"
                  element={
                    isLoggedIn ? <Navigate to="/" replace /> : <Login onLogin={handleLogin} />
                  }
                />

                <Route
                  path="/signup"
                  element={
                    isLoggedIn ? <Navigate to="/" replace /> : <Signup onLogin={handleLogin} /> // Pass handleLogin as onLogin
                  }
                />
                <Route path="/" element={<ProtectedRoute isLoggedIn={isLoggedIn} />}>
                  <Route index element={<Home />} />
                  <Route path="search" element={<SearchPage />} />
                  <Route path="chat" element={<ChatScreen />} />
                  <Route path="shopping-list" element={<ShoppingList />} />
                  <Route path="recipes" element={<Recipes />} />
                  <Route path="profile" element={<Profile onLogout={handleLogout} />} />
                  <Route path="barcodeScan" element={<BarcodeScanPage />} />
                  <Route path="nutritionGoals" element={<NutritionGoalsPage />} />
                  {/* Add route for cancellation page - potentially doesn't need to be protected? */}
                </Route>
                <Route path="/cancelar-subscricao" element={<CancelSubscription />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </SessionCheck>
          </div>
        </main>

        {isLoggedIn && (
          <footer className={`fixed bottom-0 left-0 right-0 z-10 ${mcDeTheme ? 'mc-de-app-footer bg-transparent' : 'bg-white'}`}>
            {/* Pass language, translations, and dialog handler to NavigationMenu */}
            <NavigationMenu
              language={language}
              t={t}
              onUnsubscribeClick={() => setOpenUnsubscribeDialog(true)}
            />
          </footer>
        )}

        {/* Language-specific footer for Belgian languages */}
        <LanguageFooter />

        <FeatureWelcomeDialog isLoggedIn={isLoggedIn} />

        {/* Dialog for Unsubscribe confirmation */}
        <Dialog open={openUnsubscribeDialog} handler={setOpenUnsubscribeDialog} size="xs" className={mcDeTheme ? 'mc-de-dialog' : ''}>
          <DialogHeader>{t.confirmUnsubscribe}</DialogHeader>
          <DialogBody>
            <Typography>{t.confirmUnsubscribeMessage}</Typography>
          </DialogBody>
          <DialogFooter>
            <Button color="red" onClick={handleConfirmUnsubscribe} className='mr-2'>
              {t.yes}
            </Button>
            <Button variant="text" onClick={() => setOpenUnsubscribeDialog(false)}>
              {t.cancel}
            </Button>
          </DialogFooter>
        </Dialog>

      </div>
    </Router>
  );
};

export default App;
